// src/components/EditorialAssistantInvitation.jsx
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from '../utils/axios';
import { useAuth } from '../context/AuthContext';
import {
  UserPlus,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Loader,
  Eye,
  EyeOff,
  LogIn,
  Mail
} from 'lucide-react';
import { sanitizeEmail } from '../utils/utils.global';

const disciplineOptions = [
  'Agricultural sciences',
  'Allied Health Sciences',
  'Anthropology',
  'Arts',
  'Biology',
  'Chemistry',
  'Climate Change',
  'Computer and Information Sciences',
  'Earth Sciences',
  'Engineering and Technology',
  'Environmental Sciences',
  'Epidemiology',
  'Humanities',
  'Leadership and Management',
  'Life Sciences',
  'Materials Science',
  'Mathematics',
  'Medicine',
  'Physics and Astronomy',
  'Politics',
  'Public & Global Health',
  'Social and Behavioral Sciences',
  'Statistics',
  'Theology',
  'Other'
];

const EditorialAssistantInvitation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();

  const queryParams = new URLSearchParams(location.search);
  const emailFromUrl = sanitizeEmail(queryParams.get('email')) || '';
  const token = queryParams.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [invitationValid, setInvitationValid] = useState(false);
  const [userExists, setUserExists] = useState(false);
  const [alreadyEditor, setAlreadyEditor] = useState(false);
  const [invitationEmail, setInvitationEmail] = useState(emailFromUrl);
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const [formData, setFormData] = useState({
    prefix: '',
    firstName: '',
    lastName: '',
    otherName: '',
    orcid: '',
    affiliation: '',
    affiliationCountry: '',
    affiliationCity: '',
    discipline: '',
    otherDiscipline: '',
    password: '',
    confirmPassword: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const fetchInvitation = async () => {
      if (!emailFromUrl || !token) {
        setError('Invalid invitation link');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.post('/api/personnel/invitation/assistant/details', {
          email: emailFromUrl,
          token
        });

        if (response.data.status === 'success') {
          setInvitationValid(true);
          setInvitationEmail(response.data.invitation.email || emailFromUrl);
          setUserExists(response.data.userExists);
          setAlreadyEditor(response.data.alreadyEditor);
          if (response.data.alreadyEditor) {
            setInfo('This account is already an editor. Please log in to access the editorial dashboard.');
          }
          if (!response.data.userExists) {
            setShowCreateAccount(true);
          }
        } else if (response.data.alreadyProcessed) {
          setError(response.data.message || 'This invitation has already been processed');
        } else if (response.data.status === 'expired') {
          setError(response.data.message || 'This invitation has expired');
        } else {
          setError(response.data.message || 'Invalid invitation');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Unable to load invitation. Please check your invitation link.');
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, [emailFromUrl, token]);

  const handleAccept = async () => {
    setProcessing(true);
    try {
      const response = await axios.post('/api/personnel/invitation/assistant/accept', {
        email: invitationEmail,
        token
      });

      if (response.data.status === 'success' && response.data.token && response.data.user) {
        const role = response.data.user.role || 'editor';
        const loginResult = await login(invitationEmail, '', role, response.data.token, response.data.user);
        if (loginResult.success) {
          navigate(response.data.redirectTo || '/editors/dashboard');
          return;
        }
      }

      setError(response.data.message || 'Unable to accept invitation');
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to accept invitation. Please try again.');
      setLoading(false);
    } finally {
      setProcessing(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.firstName.trim()) errors.firstName = 'First name is required';
    if (!formData.lastName.trim()) errors.lastName = 'Last name is required';
    if (formData.password.length < 8) errors.password = 'Password must be at least 8 characters';
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      errors.password = 'Password must contain uppercase, lowercase and a number';
    }
    if (formData.password !== formData.confirmPassword) errors.confirmPassword = 'Passwords do not match';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setProcessing(true);
    setError(null);

    const submitData = {
      ...formData,
      discipline: formData.discipline === 'Other' ? formData.otherDiscipline : formData.discipline,
      email: invitationEmail,
      token
    };

    try {
      const response = await axios.post('/api/personnel/invitation/create-assistant-account', submitData);

      if (response.data.status === 'success') {
        if (response.data.data?.token && response.data.data?.user) {
          const role = response.data.data.user.role || 'editor';
          const loginResult = await login(
            invitationEmail,
            '',
            role,
            response.data.data.token,
            response.data.data.user
          );
          if (loginResult.success) {
            navigate(response.data.data.redirectTo || '/editors/dashboard');
            return;
          }
        }
        setTimeout(() => {
          navigate('/portal/login', {
            state: { message: 'Account created. Please log in.' }
          });
        }, 2500);
      } else {
        setError(response.data.message || 'Failed to create account');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create account. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const inputClass = (field) =>
    `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[--asfi-purple] ${
      formErrors[field] ? 'border-red-300' : 'border-gray-300'
    }`;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white flex items-center justify-center">
        <Loader className="w-8 h-8 text-[--asfi-purple] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="h-24 bg-gradient-to-r from-[--asfi-blue] to-[--asfi-purple] flex items-center justify-center">
            <img src="/assets/images/favicon.png" alt="ASFIRJ" className="w-16 h-16 rounded-lg bg-white/10" />
          </div>

          <div className="px-8 py-6">
            <h1 className="text-2xl font-bold text-gray-800 mb-2 text-center">
              Editorial Assistant Invitation
            </h1>
            <p className="text-sm text-gray-500 mb-6 text-center">
              {invitationEmail ? (
                <span className="flex items-center justify-center gap-1">
                  <Mail className="w-4 h-4" /> {invitationEmail}
                </span>
              ) : (
                'Join the ASFI Research Journal editorial team'
              )}
            </p>

            {error && (
              <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                {error.includes('expired') || error.includes('processed') ? (
                  <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                )}
                <div>
                  <p className="text-sm text-red-700">{error}</p>
                  <button
                    onClick={() => navigate('/portal/login')}
                    className="mt-2 text-xs font-medium text-red-600 underline"
                  >
                    Go to login
                  </button>
                </div>
              </div>
            )}

            {info && (
              <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-blue-700">{info}</p>
                  <button
                    onClick={() => navigate('/editors/login')}
                    className="mt-2 inline-flex items-center text-xs font-medium text-blue-600 underline"
                  >
                    <LogIn className="w-3 h-3 mr-1" /> Log in to editorial dashboard
                  </button>
                </div>
              </div>
            )}

            {invitationValid && !alreadyEditor && !showCreateAccount && (
              <div className="text-center">
                <div className="bg-purple-50 rounded-lg p-6 mb-6">
                  <UserPlus className="w-12 h-12 text-[--asfi-purple] mx-auto mb-3" />
                  <p className="text-sm text-gray-700 mb-2">
                    You have been invited to join the editorial team as an{' '}
                    <strong>Editorial Assistant</strong>.
                  </p>
                  <p className="text-xs text-gray-500">
                    Accepting will give you access to the editorial dashboard for managing submissions.
                  </p>
                </div>
                <button
                  onClick={handleAccept}
                  disabled={processing}
                  className="w-full py-3 px-4 text-white font-medium bg-gradient-to-r from-[--asfi-blue] to-[--asfi-purple] rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 inline-flex items-center justify-center"
                >
                  {processing ? <Loader className="w-5 h-5 mr-2 animate-spin" /> : <CheckCircle className="w-5 h-5 mr-2" />}
                  Accept Invitation
                </button>
              </div>
            )}

            {showCreateAccount && invitationValid && !alreadyEditor && (
              <form onSubmit={handleCreateAccount}>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Prefix</label>
                    <input
                      type="text"
                      value={formData.prefix}
                      onChange={(e) => setFormData({ ...formData, prefix: e.target.value })}
                      placeholder="Dr."
                      className={inputClass('prefix')}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">First Name *</label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className={inputClass('firstName')}
                    />
                    {formErrors.firstName && <p className="text-xs text-red-500 mt-1">{formErrors.firstName}</p>}
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Last Name *</label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      className={inputClass('lastName')}
                    />
                    {formErrors.lastName && <p className="text-xs text-red-500 mt-1">{formErrors.lastName}</p>}
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Affiliation <span className="text-gray-400">(optional)</span></label>
                    <input
                      type="text"
                      value={formData.affiliation}
                      onChange={(e) => setFormData({ ...formData, affiliation: e.target.value })}
                      placeholder="University or Institution"
                      className={inputClass('affiliation')}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Country</label>
                    <input
                      type="text"
                      value={formData.affiliationCountry}
                      onChange={(e) => setFormData({ ...formData, affiliationCountry: e.target.value })}
                      className={inputClass('affiliationCountry')}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">City</label>
                    <input
                      type="text"
                      value={formData.affiliationCity}
                      onChange={(e) => setFormData({ ...formData, affiliationCity: e.target.value })}
                      className={inputClass('affiliationCity')}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Discipline <span className="text-gray-400">(optional)</span></label>
                    <select
                      value={formData.discipline}
                      onChange={(e) => setFormData({ ...formData, discipline: e.target.value })}
                      className={inputClass('discipline')}
                    >
                      <option value="">Select discipline</option>
                      {disciplineOptions.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                    {formData.discipline === 'Other' && (
                      <input
                        type="text"
                        value={formData.otherDiscipline}
                        onChange={(e) => setFormData({ ...formData, otherDiscipline: e.target.value })}
                        placeholder="Specify discipline"
                        className={`${inputClass('otherDiscipline')} mt-2`}
                      />
                    )}
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Password *</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className={inputClass('password')}
                        placeholder="At least 8 characters"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {formErrors.password && <p className="text-xs text-red-500 mt-1">{formErrors.password}</p>}
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Confirm Password *</label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                        className={inputClass('confirmPassword')}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {formErrors.confirmPassword && <p className="text-xs text-red-500 mt-1">{formErrors.confirmPassword}</p>}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={processing}
                  className="mt-5 w-full py-3 px-4 text-white font-medium bg-gradient-to-r from-[--asfi-blue] to-[--asfi-purple] rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 inline-flex items-center justify-center"
                >
                  {processing ? <Loader className="w-5 h-5 mr-2 animate-spin" /> : <UserPlus className="w-5 h-5 mr-2" />}
                  Create Account & Accept Invitation
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditorialAssistantInvitation;